// Imports for external libraries go here.
import React, { useRef, useEffect, useState, FC } from 'react';
import clsx from 'clsx';
import { Text, Types, Link, Image, useCheckBreakpoint, Eyebrow } from '@marriott/mi-ui-library';
import Glide from '@glidejs/glide';

// Imports for internal (to the monorepo) libraries go here,
import { StyledCardCarousel } from './CarouselContainer.styles';
import { CarouselProps, CarouselDataProps, DiningPlanDetail } from './CarouselContainer.types';
import { AmenitiesModal } from '../../organisms/AmenitiesModal';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import DOMPurify from 'isomorphic-dompurify';
import { getOptimizedImageUrl } from '../../util/helpers';
import { SurveyWrapper } from '../../molecules/SurveyWrapper';
import { brandColorSchemeGenerator, getSubcategoryLabel } from '../../util/constants';

export const CarouselContainer: FC<CarouselProps> = (props: CarouselProps) => {
  const { amenitiesDetails, componentType, arrivalDate, departureDate, eventId } = props;
  const isMobileViewPort = !useCheckBreakpoint('viewportM');

  const { formData, setFormData } = useCreateSurveyStore();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const glideRef = useRef<HTMLDivElement | any>(null);
  const cardCount = amenitiesDetails?.length;
  const componentId = componentType;
  const noOfCards = cardCount / 2;
  const cardsPerView = noOfCards;
  const [openText, setopenText] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubtitle, setSelectedSubtitle] = useState<string>('');
  const [selectedDescription, setSelectedDescription] = useState<string>('');
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [colorSchemeforPage, setcolorSchemeforPage] = useState('');
  const initialAddedToPlan = amenitiesDetails?.map((_, index) => {
    // Check if formData.experiences_plan_details exists and has an item with the current index
    let added = false;
    // Check if formData.experiences_plan_details has an item with the current index
    if (componentType === 'Experience' && formData.experiences_plan_details) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      added = formData.experiences_plan_details.some((item: any) => item.selected_index === index);
    }

    // If not added, check formData.dining_plan_details as well
    if (!added && componentType === 'Dining' && formData.dining_plan_details.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      added = formData.dining_plan_details.some((item: any) => item.selected_index === index);
    }

    return added;
  });
  const [addedToPlan, setAddedToPlan] = useState<boolean[]>(initialAddedToPlan);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedModalIndex, setSelectedModalIndex] = useState<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        if (Glide.settings.rewind) return;
        Glide.on(['mount.after', 'run'], () => {
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') continue;
            const left = controlItem?.querySelector('.glide__arrow--left');
            if (left) {
              if (Glide.index === 0) {
                left.classList.add('is-disabled');
              } else {
                left.classList.remove('is-disabled');
              }
            }
            const right = controlItem?.querySelector('.glide__arrow--right');
            if (right) {
              if (Glide.index === Components.Sizes.length - Glide.settings.perView) {
                right.classList.add('is-disabled');
              } else {
                right.classList.remove('is-disabled');
              }
            }
          }
        });
      },
    };
  };

  useEffect(() => {
    // console.log('brandcode in CarouselContainer:', props?.brandCode, formData?.brandCode);
    setcolorSchemeforPage(brandColorSchemeGenerator(props?.brandCode || formData?.brandCode, 'carousel'));
    // console.log('color scheme for carousel:', colorSchemeforPage);
    const carouselRootSelector = `#${eventId}`;
    if (document?.querySelector(carouselRootSelector)) {
      try {
        new Glide(carouselRootSelector, {
          type: 'slider',
          bound: false,
          perView: 3,
          startAt: 0,
          rewind: false,
          gap: 17,
          dragThreshold: false,
          breakpoints: {
            767: {
              perView: 1,
            },
          },
        }).mount({ ArrowDisabler });

        const addArrowEventListener = (arrowId: string, clickTrack: string) => {
          const arrow = document?.querySelector(`#${arrowId}`);
          if (arrow) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            arrow.addEventListener('touchstart', (e: any) => {
              document.getElementById(clickTrack)?.click();
              e.preventDefault();
              e.stopImmediatePropagation();
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            arrow.addEventListener('click', (e: any) => {
              document.getElementById(clickTrack)?.click();
              e.stopImmediatePropagation();
            });
          }
        };

        addArrowEventListener(`${eventId}_left_arrow`, 'left-arrow-click-track');
        addArrowEventListener(`${eventId}_right_arrow`, 'right-arrow-click-track');
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
  }, [componentId, cardsPerView]);

  const modalHandler = (subtitle: string, index: number, desc: string, reservation: string, subcategory: string) => {
    setSelectedModalIndex(index);
    setSelectedSubtitle(subtitle);
    setSelectedDescription(desc);
    setSelectedTitle(subtitle);
    setSelectedSubCategory(subcategory);
    if (reservation === '1') {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAddToPlan = (index: number, title: string, requirement: string, subcategory: string) => {
    const updatedAddedToPlan = [...addedToPlan];
    updatedAddedToPlan[index] = true;
    if (requirement !== '1' || requirement === null) {
      const amenitiesDataList = formData?.prearrivalformamenitiesaddedtoplan;
      const newDetail: DiningPlanDetail = {
        dining_location: title,
        selected_index: index,
        reservation_req: '0',
      };
      const updatedFormDataList = [...amenitiesDataList, subcategory != '' ? getSubcategoryLabel(subcategory) : title];
      const detailKey = componentType !== 'Experience' ? 'dining_plan_details' : 'experiences_plan_details';

      setFormData({
        ...formData,
        [detailKey]: [...(formData[detailKey] || []), newDetail],
        prearrivalformamenitiesaddedtoplan: updatedFormDataList,
      });
    }
    setAddedToPlan(updatedAddedToPlan);
  };

  const handleRemoveFromPlan = (indexToRemove: number, data: CarouselDataProps) => {
    // Create a copy of the formData object
    const newFormData = { ...formData };

    // Remove the title of the item from the amenitiesDataList
    const newAmenitiesList = newFormData?.prearrivalformamenitiesaddedtoplan.filter(
      (title: string) => title !== (data?.subcategory != '' ? getSubcategoryLabel(data?.subcategory) : data?.title)
    );

    // Remove the experience/dining matching the item from formData based on its category
    if (data.category === 'activities_page' && newFormData?.experiences_plan_details) {
      newFormData.experiences_plan_details = newFormData.experiences_plan_details.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => item.dining_location !== data?.title
      );
    } else if (data.category === 'dining_page' && newFormData?.dining_plan_details) {
      newFormData.dining_plan_details = newFormData.dining_plan_details.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => item.dining_location !== data?.title
      );
    }

    // Update formData in the store with the filtered list
    setFormData({
      ...newFormData,
      prearrivalformamenitiesaddedtoplan: newAmenitiesList, // Sync with new amenities list
    });

    // Update the addedToPlan array
    const updatedAddedToPlan = [...addedToPlan];
    updatedAddedToPlan[indexToRemove] = false;
    setAddedToPlan(updatedAddedToPlan);
  };

  const handleButton = () => {
    const updatedAddedToPlan = [...addedToPlan];
    updatedAddedToPlan[selectedModalIndex] = false;
    setAddedToPlan(updatedAddedToPlan);
    handleCloseModal();
  };

  function isInternalUrl(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      // Only allow exact domain or subdomains
      return parsed.hostname === 'marriott.com' || parsed.hostname.endsWith('.marriott.com');
    } catch (e) {
      console.warn('Invalid URL passed:', url, e);
      return false;
    }
  }

  const getSanitizedMarkup = (html: string) => {
    const sanitizedNode1 = DOMPurify.sanitize(html, { RETURN_DOM: true }) as HTMLElement;

    return sanitizedNode1.innerHTML;
  };

  return (
    <>
      <StyledCardCarousel className="pt-2">
        <div id={eventId} className="glide" ref={glideRef}>
          <div className={clsx('glide__track')} data-glide-el="track">
            <ul id={`${eventId}__slides`} className={clsx('glide__slides')}>
              {amenitiesDetails?.map((data: CarouselDataProps, index: number) => {
                const imageURL =
                  componentType === 'MBTA' ? getOptimizedImageUrl(data.imageUrl) : getOptimizedImageUrl(data.image);
                const mbtaTitle = componentType === 'MBTA' ? data?.name : '';
                const mbtaDescription = componentType === 'MBTA' ? getSanitizedMarkup(data?.description) : '';
                const mbtaDescriptionPlainText = mbtaDescription
                  ? DOMPurify.sanitize(mbtaDescription, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
                  : '';
                const shouldRenderDescription =
                  componentType !== 'MBTA' || mbtaTitle.trim() !== mbtaDescriptionPlainText.trim();
                return (
                  <li
                    className={clsx('glide__slide', 'customClassLi')}
                    data-glide-index={index}
                    key={index}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <div className="align-items-center">
                      <div style={{ position: 'relative' }}>
                        <Image
                          loading="lazy"
                          defaultImageURL={data?.image ? data?.image : getOptimizedImageUrl(data.imageUrl)}
                          renditions={[
                            {
                              renditionPath: imageURL,
                              mediaValue: '576px',
                              width: 0,
                              dynamic: true,
                              damPath: imageURL,
                              mediaQuery: 'max-width',
                              height: 0,
                            },
                          ]}
                          altText={componentType === 'MBTA' ? data?.name : data?.image_alt_text}
                          customClass="card__image-section-size-img align-self-stretch"
                        />
                        {openText && componentType !== 'MBTA' && (
                          <button
                            className="m-button-secondary addButton"
                            onClick={() => {
                              if (addedToPlan[index]) {
                                handleRemoveFromPlan(index, data);
                              } else {
                                handleAddToPlan(index, data.title, data.reservation_requirement, data.subcategory);
                                modalHandler(
                                  data.title,
                                  index,
                                  data.text,
                                  data.reservation_requirement,
                                  data.subcategory
                                );
                              }
                            }}
                          >
                            {addedToPlan[index] ? 'Remove from Plan' : 'Add to Plan'}
                          </button>
                        )}
                        {openText && componentType === 'MBTA' && (
                          <a
                            href={
                              data?.url +
                              '?marshaCode=' +
                              formData.marshaCode +
                              '&utm_source=marriott_com&utm_medium=travel_form&utm_campaign=prearrival&prearrival=expB&from=' +
                              arrivalDate?.replaceAll('-', '') +
                              '&to=' +
                              departureDate?.replaceAll('-', '')
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            <button className="m-button-secondary addButton">Book Now</button>
                          </a>
                        )}
                      </div>
                      <SurveyWrapper
                        colorScheme={colorSchemeforPage}
                        env_api_tile_error_info=""
                        className="carousel-text-wrapper"
                      >
                        <div className="carousel-text">
                          {data.subcategory && data.subcategory != 'null' && (
                            <span className="ebrowbg">
                              <Eyebrow
                                customClass="ebrowfw"
                                text={
                                  data.subcategory && data.subcategory !== 'null'
                                    ? getSubcategoryLabel(data.subcategory)
                                    : ''
                                }
                              />
                            </span>
                          )}
                          <header className="header">
                            <div className="header-text">{componentType == 'MBTA' ? data?.name : data.title}</div>
                          </header>
                          {shouldRenderDescription && (
                            <Text
                              dangerouslySetInnerHTML={{
                                __html:
                                  typeof data === 'string'
                                    ? data
                                    : componentType == 'MBTA'
                                    ? mbtaDescription
                                    : data?.text,
                              }}
                              element={Types.tags.paragraph}
                              fontSize={Types.size.extraSmall}
                              customClass="text-left glide__slide-msg description sub-title"
                            />
                          )}
                          {data.cta_url && (
                            <Link
                              text={data.cta_label}
                              linkClassName="m-details-page-TnC m-link-tertiary-button-external custom_click_track"
                              linkHref={data.cta_url}
                              linkType="internal"
                              target="_blank"
                              custom_click_track_value={`${
                                data?.category?.split('_')[0] === 'activities'
                                  ? 'Experiences'
                                  : data?.category?.split('_')[0].charAt(0).toUpperCase() +
                                    data?.category?.split('_')[0].slice(1)
                              } Reservation Tile|${data.cta_label}|${
                                isInternalUrl(data.cta_url) ? 'internal' : 'external'
                              }`}
                            ></Link>
                          )}
                        </div>
                      </SurveyWrapper>
                    </div>
                  </li>
                );
              })}
            </ul>
            {(cardCount > 3 || (isMobileViewPort && cardCount > 1)) && (
              <div className={clsx('d-flex align-items-center justify-content-center controls_wrapper')}>
                <div className="carouselControlType3b">
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className="glide__arrow--left left-arrow"
                      data-glide-dir="<"
                      aria-label="Previous"
                      data-content="Previous"
                      id={`${componentId}_left_arrow`}
                    >
                      <span className="icon-arrow-left"></span>
                    </button>
                  </div>
                  <div data-glide-el="controls[nav]">
                    {Array.from({ length: cardCount - (cardsPerView - 1) }, (_, i) => (
                      <button
                        className=""
                        data-glide-dir={i}
                        aria-label={`control-button-${i}`}
                        key={cardsPerView + i}
                      ></button>
                    ))}
                  </div>
                  <div className="glide__arrows" data-glide-el="controls">
                    <button
                      className="glide__arrow--right right-arrow"
                      data-glide-dir=">"
                      aria-label="Next"
                      id={`${componentId}_right_arrow`}
                    >
                      <span className="icon-arrow-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </StyledCardCarousel>

      {modalOpen && (
        <AmenitiesModal
          onClose={handleCloseModal}
          componentType={componentType}
          subtitle={selectedSubtitle}
          desc={selectedDescription}
          index={selectedModalIndex}
          handleButton={handleButton}
          arrivalDate={arrivalDate}
          departureDate={departureDate}
          title={selectedTitle}
          subcategory={selectedSubCategory}
        />
      )}
    </>
  );
};
